import React, {useEffect} from 'react';
import {BoundSearchQuery} from "../../../../types/common/BoundSearchQuery";
import {SortFilterDateRange, SortFilterDateRangeProps} from "../../reports/sortFilter/SortFilterDateRange";
import {APPLY_DATES_PARAM, DateRangeChange, hasIdSearch, isApplyDates} from "../../../../util/ReportDateRangeUtils";

type BoundDateRangeFilterProps<T> = Pick<SortFilterDateRangeProps, 'showEndDate' | 'disableFuture' | 'idSearchLabel'> & {
  query: T;
  onChange: (newQuery: T) => void;
  /** Query key holding the start date (default `startDate`). */
  startDateName?: keyof T & string;
  /** Query key holding the end date (default `endDate`). */
  endDateName?: keyof T & string;
  /**
   * Query keys that, when filled, search all dates. Pair with `withDateRangeBypass` on the request,
   * passing `[startDateName, endDateName]` as its date keys when they aren't the defaults.
   */
  dateBypassKeys?: readonly string[];
};

const BoundDateRangeFilter = <T extends BoundSearchQuery>({
  query,
  onChange,
  startDateName = 'startDate' as keyof T & string,
  endDateName = 'endDate' as keyof T & string,
  dateBypassKeys = [],
  ...props
}: BoundDateRangeFilterProps<T>) => {
  const idSearchActive = hasIdSearch(query, dateBypassKeys);
  const applyDates = isApplyDates(query);

  // A cleared ID search drops the override, so the next ID search starts from all dates again.
  useEffect(() => {
    if (!idSearchActive && applyDates) {
      onChange({[APPLY_DATES_PARAM]: undefined} as unknown as T);
    }
  }, [idSearchActive, applyDates, onChange]);

  const handleChange = (change: DateRangeChange) => onChange({
    ...('startDate' in change && {[startDateName]: change.startDate}),
    ...('endDate' in change && {[endDateName]: change.endDate}),
    ...(change.applyDates !== undefined && {[APPLY_DATES_PARAM]: change.applyDates ? 'true' : undefined}),
  } as unknown as T);

  return <SortFilterDateRange
    startDate={query?.[startDateName] as string | undefined}
    endDate={query?.[endDateName] as string | undefined}
    onChange={handleChange}
    idSearchActive={idSearchActive}
    applyDates={applyDates}
    {...props}
  />;
};

export {BoundDateRangeFilter};
